// Módulo de Psicología de Trading — 3 lecciones largas en español.
// Cada lección tiene `contenido` estructurado (cuando hay sub-items) o
// `contenido_texto` markdown plano para el cuerpo. El page detail soporta
// ambos formatos.

export const LECCIONES_PSICOLOGIA = [
  {
    id: "los-7-errores",
    numero: 1,
    titulo: "Los 7 Errores Psicológicos que Destruyen Cuentas",
    duracion: "12 min",
    descripcion: "El 95% de los traders pierden dinero. No es por falta de conocimiento técnico — es por falta de disciplina mental. Estos son los 7 errores psicológicos más comunes y cómo evitarlos.",
    contenido: [
      { tipo: "error", numero: 1, nombre: "FOMO (Fear Of Missing Out)",
        descripcion: "Entrar en un trade porque el precio ya ha subido mucho y tienes miedo de perderte el movimiento. Resultado: compras en máximos y eres la última persona que entra antes de que el precio caiga.",
        sintomas: ["Entras sin analizar", "El precio ya subió >5%", "No tienes plan de SL definido", "Sientes urgencia"],
        solucion: "Acepta que siempre habrá otra oportunidad. Si no entraste al inicio del movimiento, el trade YA NO ES TUYO. Espera el siguiente setup.",
        ejercicio: "La próxima vez que sientas FOMO, escribe en tu diario: «El precio ya subió X%. Si entro ahora, mi R:R es de ___». Si el R:R es menor de 1:2, NO entres." },
      { tipo: "error", numero: 2, nombre: "Revenge Trading",
        descripcion: "Después de una pérdida, abres inmediatamente otro trade para recuperar el dinero perdido. Operas desde la emoción, no desde la estrategia. Resultado: la segunda pérdida suele ser mayor que la primera.",
        sintomas: ["Acabas de perder un trade", "Entras sin esperar setup", "Aumentas el tamaño de posición", "Sientes rabia o frustración"],
        solucion: "Regla inquebrantable: después de 2 pérdidas consecutivas, PARA de operar ese día. El mercado estará ahí mañana.",
        ejercicio: "Implementa la regla de los 2 stops: si activas 2 stop losses en un día, cierra la plataforma. Sin excepciones." },
      { tipo: "error", numero: 3, nombre: "Mover el Stop Loss",
        descripcion: "El precio se acerca al stop loss y en vez de dejarlo activar, lo mueves más abajo «esperando que se recupere». Resultado: una pérdida del 1% se convierte en una del 5-10%.",
        sintomas: ["El precio se acerca al SL", "Piensas «solo un poco más»", "Has movido el SL más de una vez", "El trade dura mucho más de lo planeado"],
        solucion: "El stop loss es SAGRADO. Si lo pusiste en el lugar correcto al analizar, no lo muevas. Si lo mueves, toda tu gestión de riesgo se vuelve inútil.",
        ejercicio: "Pon una nota en tu monitor: «SL = PLAN. PLAN = SL. NO MOVER»." },
      { tipo: "error", numero: 4, nombre: "Overtrading",
        descripcion: "Operar en exceso buscando constantemente trades. El aburrimiento o la euforia llevan a entrar en setups de baja calidad. Más trades = más comisiones = más exposición al riesgo = más agotamiento mental.",
        sintomas: ["Llevas más de 5 trades en un día", "Buscas trades activamente", "Cambias de timeframe buscando setups", "No puedes estar sin posición abierta"],
        solucion: "Calidad sobre cantidad. Define cuántos trades es tu máximo diario (recomendado: 2-3). Si los has alcanzado, para. Los mejores traders hacen pocos trades pero muy selectivos.",
        ejercicio: "Lleva un registro de todos tus trades. Al final del mes, compara win rate de los primeros trades del día vs los últimos. Verás que los últimos son peores." },
      { tipo: "error", numero: 5, nombre: "Salir Antes del Take Profit",
        descripcion: "El trade va bien, llevas +3% y decides cerrar antes de llegar al TP porque «ya ganaste suficiente» o por miedo a que se revierta. Resultado: tu R:R real es 1:0.8 en vez de 1:3.",
        sintomas: ["Cierras trades ganadores pronto", "Tienes miedo a que el beneficio desaparezca", "Tu beneficio promedio es menor que tu riesgo promedio"],
        solucion: "Confía en tu análisis. Si pusiste el TP en un nivel técnico válido, déjalo correr. Puedes usar parciales (cerrar 50% en el primer objetivo) para reducir la ansiedad.",
        ejercicio: "En tu diario, anota el precio al que cerraste y el precio que alcanzó realmente. Cuantifica cuánto «dejaste sobre la mesa» por no respetar el TP." },
      { tipo: "error", numero: 6, nombre: "Exceso de Confianza tras Rachas Ganadoras",
        descripcion: "Después de 5-6 trades ganadores seguidos aumentas el tamaño de posición o bajas los estándares de análisis porque «estás en racha». La soberbia precede a la caída. Siempre.",
        sintomas: ["Llevas varios trades ganadores", "Aumentas el tamaño sin justificación", "Analizas menos cada trade", "Sientes que «no puedes perder»"],
        solucion: "El mercado no sabe que llevas una racha ganadora. Cada trade es independiente. Mantén el mismo % de riesgo siempre.",
        ejercicio: "Define por adelantado cuándo subirás el tamaño de posición: solo cuando el capital haya crecido un 20%. No antes." },
      { tipo: "error", numero: 7, nombre: "No Seguir el Plan de Trading",
        descripcion: "Tienes un plan pero en el momento de operar lo ignoras porque «esta vez es diferente». Sin plan = sin disciplina = sin resultados consistentes. El plan no es una sugerencia, es la ley.",
        sintomas: ["Cambias de estrategia en el momento", "Operas por intuición", "Tus resultados son inconsistentes", "No tienes reglas escritas"],
        solucion: "Escribe tus reglas de trading en papel y ponlas frente al monitor. Antes de cada trade, verifica que cumple TODAS las condiciones. Si no las cumple, no hay trade.",
        ejercicio: "Escribe ahora mismo tu plan de trading: condiciones de entrada, SL, TP, máximo de trades por día, capital máximo a arriesgar. Una página, reglas simples, sin excepciones." },
    ],
  },
  {
    id: "reglas-trading-personal",
    numero: 2,
    titulo: "Cómo Crear Tu Reglamento de Trading Personal",
    duracion: "8 min",
    descripcion: "Un trader sin reglas es un jugador. La diferencia entre un trader profesional y un perdedor no es el conocimiento técnico — es la disciplina para seguir un conjunto de reglas en cualquier condición de mercado.",
    contenido_texto: `Un trader sin reglas es un jugador. La diferencia entre un trader profesional y un perdedor no es el conocimiento técnico — es la disciplina para seguir un conjunto de reglas en cualquier condición de mercado.

TUS REGLAS DEBEN INCLUIR:

1. CRITERIOS DE ENTRADA
   «Solo entro cuando: [lista tus condiciones específicas]».
   Ejemplo: «Solo entro en tendencia alcista (4H), en pullback a OB, con confirmación de CHoCH en 15m, con R:R mínimo 1:2».

2. GESTIÓN DEL RIESGO
   • Mi riesgo máximo por trade es X%.
   • Mi pérdida máxima diaria es X%.
   • Si pierdo X% en la semana, no opero más esa semana.

3. CONDICIONES DE MERCADO
   • No opero 30 minutos antes de noticias importantes.
   • No opero los lunes antes de las 9 AM.
   • Solo opero en sesión de Londres o Nueva York.

4. GESTIÓN EMOCIONAL
   • Si activo 2 stops seguidos, paro ese día.
   • Reviso el plan antes de cada trade.
   • No opero si estoy cansado, enfadado o estresado.

5. REVISIÓN Y MEJORA
   • Cada domingo reviso todos los trades de la semana.
   • Cada mes calculo mis estadísticas: win rate, avg R:R, drawdown.

Imprime tu plan. Pégalo donde lo veas todos los días. Léelo antes de cada sesión. Si lo respetas durante 90 días, te convertirás en un trader distinto.`,
  },
  {
    id: "diario-emocional",
    numero: 3,
    titulo: "El Diario de Trading Emocional",
    duracion: "6 min",
    descripcion: "Los mejores traders del mundo llevan diario. No solo registran los números (entrada, salida, P&L) sino también su estado mental.",
    contenido_texto: `Los mejores traders del mundo llevan diario. No solo registran los números (entrada, salida, P&L) sino también su estado mental.

PLANTILLA DE ENTRADA DE DIARIO:

FECHA: _______________
ESTADO EMOCIONAL ANTES DE OPERAR (1-10): ___
Notas: _______________

TRADE
• Par: ___ — Dirección: LONG / SHORT
• Timeframe de análisis: ___
• Razón de entrada: _______________
• Setup identificado: _______________
• Entrada: ___  ·  SL: ___  ·  TP: ___
• R:R calculado: ___

GESTIÓN DEL TRADE
• ¿Seguiste el plan? SÍ / NO
• Si no: ¿Por qué? _______________
• Emociones durante el trade: _______________

RESULTADO
• Precio de salida: ___  ·  P&L: ___
• ¿Respetaste el SL? SÍ / NO
• ¿Respetaste el TP? SÍ / NO

LECCIÓN DEL TRADE
• ¿Qué hiciste bien? _______________
• ¿Qué mejorarías? _______________

Repítelo cada operación. Al cabo de 50 trades, las pautas que destruyen tu cuenta saltarán a la vista. Y entonces podrás arreglarlas.`,
  },
];

export const findLeccion = (id) =>
  LECCIONES_PSICOLOGIA.find((l) => l.id === id);
